import { supabase } from './supabase';
import { getUser } from './auth';

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const isPlaceholderName = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return !normalized || normalized === 'trader' || normalized === 'subscriber' || normalized === 'unknown';
};

const resolveSubscriberName = (profileName, explicitName) => {
  if (!isPlaceholderName(explicitName)) return explicitName.trim();
  if (!isPlaceholderName(profileName)) return profileName.trim();
  return 'Subscriber';
};

const getSubscribedStage = async () => {
  const { data, error } = await supabase
    .from('crm_pipeline_stages')
    .select('id')
    .eq('name', 'Subscribed')
    .single();

  if (error) throw error;
  return data;
};

export const syncFundSubscribersToCrm = async ({
  subscribers = [],
  existingSubscriptions = [],
  existingContacts = [],
}) => {
  if (!subscribers.length) {
    return { created: 0, updated: 0 };
  }

  const subscribedStage = await getSubscribedStage();
  const manager = await getUser();
  const uniqueUserIds = [...new Set(subscribers.map((subscriber) => subscriber.user_id).filter(Boolean))];

  let profileMap = {};
  if (uniqueUserIds.length) {
    const { data: profiles, error: profileError } = await supabase
      .from('profiles')
      .select('id, username, phone')
      .in('id', uniqueUserIds);

    if (profileError) throw profileError;
    profileMap = Object.fromEntries((profiles || []).map((profile) => [profile.id, profile]));
  }

  const contactsBySignature = new Map();
  const contactsById = new Map();
  existingContacts.forEach((contact) => {
    contactsById.set(contact.id, contact);
    const nameKey = (contact.full_name || '').trim().toLowerCase();
    const phoneKey = (contact.phone || '').trim().toLowerCase();
    const signature = `${nameKey}::${phoneKey}`;
    if (nameKey && !contactsBySignature.has(signature)) {
      contactsBySignature.set(signature, contact);
    }
    if (nameKey && phoneKey === '' && !contactsBySignature.has(`${nameKey}::`)) {
      contactsBySignature.set(`${nameKey}::`, contact);
    }
  });

  const subscriptionsBySubscriberStateId = new Map();
  const subscriptionsByProfileId = new Map();
  existingSubscriptions.forEach((subscription) => {
    if (subscription.converted_subscriber_state_id) {
      subscriptionsBySubscriberStateId.set(subscription.converted_subscriber_state_id, subscription);
    }
    if (subscription.converted_profile_id) {
      subscriptionsByProfileId.set(subscription.converted_profile_id, subscription);
    }
  });

  let created = 0;
  let updated = 0;

  for (const subscriber of subscribers) {
    const profile = profileMap[subscriber.user_id] || {};
    const subscriberName = resolveSubscriberName(profile.username, subscriber.profiles?.username);
    const phone = profile.phone || null;
    const signature = `${subscriberName.trim().toLowerCase()}::${(phone || '').trim().toLowerCase()}`;

    const existingSubscription =
      subscriptionsBySubscriberStateId.get(subscriber.id) ||
      subscriptionsByProfileId.get(subscriber.user_id) ||
      null;

    let contact = existingSubscription?.primary_contact_id
      ? contactsById.get(existingSubscription.primary_contact_id) || null
      : null;

    if (!contact) {
      contact = contactsBySignature.get(signature) || contactsBySignature.get(`${subscriberName.trim().toLowerCase()}::`) || null;
    }

    if (!contact) {
      const { data: newContact, error: contactError } = await supabase
        .from('crm_contacts')
        .insert({
          full_name: subscriberName,
          phone,
          preferred_channel: phone ? 'phone' : 'email',
          status: 'active',
          owner_user_id: manager?.id || null,
        })
        .select()
        .single();

      if (contactError) throw contactError;
      contact = newContact;
      contactsById.set(contact.id, contact);
      contactsBySignature.set(signature, contact);
      contactsBySignature.set(`${subscriberName.trim().toLowerCase()}::`, contact);
    } else if (isPlaceholderName(contact.full_name) && !isPlaceholderName(subscriberName)) {
      const { data: updatedContact, error: contactUpdateError } = await supabase
        .from('crm_contacts')
        .update({ full_name: subscriberName, phone: contact.phone || phone || null })
        .eq('id', contact.id)
        .select()
        .single();

      if (contactUpdateError) throw contactUpdateError;
      contact = updatedContact;
      contactsById.set(contact.id, contact);
      contactsBySignature.set(signature, contact);
      contactsBySignature.set(`${subscriberName.trim().toLowerCase()}::`, contact);
    }

    const payload = {
      primary_contact_id: contact.id,
      stage_id: subscribedStage.id,
      subscriber_name: subscriberName,
      subscriber_type: 'individual',
      target_amount: toNumber(subscriber.invested_amount),
      probability: 100,
      source: existingSubscription?.source || 'fund_sync',
      notes: existingSubscription?.notes || 'Auto-synced from the active fund subscriber roster.',
      expected_close_date: subscriber.join_date || null,
      converted_profile_id: subscriber.user_id,
      converted_subscriber_state_id: subscriber.id,
    };

    if (existingSubscription) {
      const shouldUpdate =
        existingSubscription.primary_contact_id !== payload.primary_contact_id ||
        existingSubscription.stage_id !== payload.stage_id ||
        existingSubscription.subscriber_name !== payload.subscriber_name ||
        toNumber(existingSubscription.target_amount) !== toNumber(payload.target_amount) ||
        existingSubscription.converted_profile_id !== payload.converted_profile_id ||
        existingSubscription.converted_subscriber_state_id !== payload.converted_subscriber_state_id ||
        existingSubscription.expected_close_date !== payload.expected_close_date ||
        existingSubscription.source !== payload.source;

      if (shouldUpdate) {
        const { error: updateError } = await supabase
          .from('crm_subscriptions')
          .update(payload)
          .eq('id', existingSubscription.id);

        if (updateError) throw updateError;
        updated += 1;
      }
      continue;
    }

    const { data: createdSubscription, error: subscriptionError } = await supabase
      .from('crm_subscriptions')
      .insert({
        ...payload,
        owner_user_id: manager?.id || null,
      })
      .select()
      .single();

    if (subscriptionError) throw subscriptionError;

    await supabase.from('crm_activities').insert({
      subscription_id: createdSubscription.id,
      contact_id: contact.id,
      type: 'sync',
      direction: 'internal',
      subject: 'Synced from fund subscriber roster',
      body: 'A CRM subscriber relationship was auto-created from the active HWM fund subscriber record.',
      created_by: manager?.id || null,
      metadata: {
        synced_profile_id: subscriber.user_id,
        synced_subscriber_state_id: subscriber.id,
      },
    });

    created += 1;
  }

  return { created, updated };
};
